import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Popover } from '@amzn/storm-ui';
import { selectHeaderState, selectIsBackUpNav } from '../../store/slice';
import { RootState } from 'src/app/store';
import { IA2AccountDropdown } from './IA2AccountDropdown';
import { AccountInfo } from './AjaxFetchAccountInfo';
import { AccountDropdownWrapper } from './styles';
import { AccountSwitcherPublisher } from 'src/commons/utils/metrics';
import { IA2AccountDropdownTrigger } from './IA2AccountDropdownTrigger';
import { useAccountDropdownContext } from './AccountDropdownProvider';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';

const MOUNT_TO_INTERACTIVE_METRIC = 'MountToInteractive';
const MOUNT_TO_INTERACTIVE_WHALE_METRIC = 'MountToInteractive.Whale';
const MOUNT_TO_INTERACTIVE_NON_WHALE_METRIC = 'MountToInteractive.NonWhale';

interface AccountDropdownProps {
  /*
   * When adding hasConsoleBanner (https://sim.amazon.com/issues/CCX-15314)
   * adjust max height of AccountDropdownWrapper
   */
  currentAccountInfo: AccountInfo;
  isBackUpNav: boolean;
}

export const AccountDropdown: React.VFC<AccountDropdownProps> = ({
  currentAccountInfo,
  isBackUpNav,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    accountCounter,
    loadingStatus,
    nextToken = null,
  } = useAccountDropdownContext();
  const isTrimT1Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM,
  );
  const isTrimT2Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM_T2,
  );
  const isTrimDialedUp = isTrimT1Enabled || isTrimT2Enabled;
  const mountedAt = useRef<number>();
  const hasPublishedInteractiveLatency = useRef(false);
  if (mountedAt.current === undefined) {
    mountedAt.current = performance.now();
  }

  useEffect(() => {
    const isReady = loadingStatus === 'success' && accountCounter !== 0;
    const isError = loadingStatus === 'error';
    if ((!isReady && !isError) || hasPublishedInteractiveLatency.current) {
      return;
    }

    hasPublishedInteractiveLatency.current = true;
    const mountToInteractive = Math.round(
      performance.now() - mountedAt.current!,
    );

    // A failure is the heavy/whale case in either arm (control's BTAS timeout,
    // or a Trim error). On a clean success the cursor decides whale — treatment
    // only, since control has no cursor and so is always non-whale.
    const isWhale = isError || (isTrimDialedUp && nextToken !== null);
    AccountSwitcherPublisher.publishTimerMonitor(
      MOUNT_TO_INTERACTIVE_METRIC,
      mountToInteractive,
    );
    AccountSwitcherPublisher.publishTimerMonitor(
      isWhale
        ? MOUNT_TO_INTERACTIVE_WHALE_METRIC
        : MOUNT_TO_INTERACTIVE_NON_WHALE_METRIC,
      mountToInteractive,
    );
  }, [accountCounter, loadingStatus, nextToken, isTrimDialedUp]);

  const currentMarketplaceId = currentAccountInfo.currentMarketplaceId;
  const advertisingAccountsDataTaktId =
    currentMarketplaceId === currentAccountInfo?.currentMarketplaceId
      ? 'aac-account-links-same-mp'
      : `aac-account-links-different-mp-${currentMarketplaceId}`;

  const handleToggle = () => {
    if (isOpen) {
      AccountSwitcherPublisher.publishCounterMonitor('Close', 1);
    } else {
      AccountSwitcherPublisher.publishCounterMonitor('Open', 1);
    }
    setIsOpen(!isOpen);
  };

  const handleClose = () => setIsOpen(false);

  return (
    <Popover
      isOpen={isOpen}
      autoFocus={true}
      trigger={
        <IA2AccountDropdownTrigger
          isOpen={isOpen}
          handleToggle={handleToggle}
        />
      }
      position="bottom"
      align="start"
      strategy="fixed"
      onCloseButtonClick={handleClose}
      spacing={0}
      padding="none"
      data-ccx-e2e-id="aac-account-dropdown-wrapper"
    >
      {!isBackUpNav && (
        <AccountDropdownWrapper>
          <IA2AccountDropdown
            advertisingAccountsDataTaktId={advertisingAccountsDataTaktId}
          />
        </AccountDropdownWrapper>
      )}
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => {
  const headerState = selectHeaderState(state);
  const isBackUpNav = selectIsBackUpNav(state);
  return {
    currentAccountInfo: headerState.currentAccountInfo as AccountInfo,
    isBackUpNav: isBackUpNav,
  };
};

export default connect(mapStateToProps)(AccountDropdown);
